import React, { useState } from 'react';
import { API } from '../api';
import { formatDate } from '../utils/dateUtils';
import Pagination from './Pagination';
import { formatCount, formatHoursValue, formatMoney, formatPaymentStatus } from '../utils/adminFormatters';
import './RecentPurchases.css';

const RecentPurchases = ({ onUserClick }) => {
  const [purchases, setPurchases] = React.useState([]);
  const [topTariffs, setTopTariffs] = React.useState([]);
  const [pagination, setPagination] = React.useState({ page: 1, limit: 20, total: 0 });
  const [loading, setLoading] = React.useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const topLimit = 5;

  const loadPurchases = React.useCallback(async (page = 1) => {
    setLoading(true);
    const res = await API.finance.getTariffPurchases({ page, limit: itemsPerPage, topLimit });
    if (res.success) {
      setPurchases(Array.isArray(res.data?.data) ? res.data.data : []);
      setTopTariffs(Array.isArray(res.data?.topTariffs) ? res.data.topTariffs : []);
      setPagination(res.data?.pagination || { page, limit: itemsPerPage, total: 0 });
      setCurrentPage(page);
    }
    setLoading(false);
  }, []);

  React.useEffect(() => {
    const loadInitial = async () => {
      setLoading(true);
      await loadPurchases(1);
    };

    loadInitial();
  }, [loadPurchases]);

  const totalPages = Math.ceil((pagination.total || 0) / (pagination.limit || itemsPerPage));

  if (loading) {
    return (
      <div className="recent-purchases">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Загрузка покупок...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="recent-purchases">
      <div className="recent-purchases-header">
        <h2>Покупки тарифов</h2>
        <div className="purchases-meta">Только данные из `/admin/finance/tariff-purchases`</div>
      </div>
      {topTariffs.length > 0 && (
        <div className="top-tariffs-grid">
          {topTariffs.map((tariff) => (
            <div key={`${tariff.tariffId}-${tariff.rank}`} className="top-tariff-card">
              <div className="top-tariff-rank">#{tariff.rank}</div>
              <div className="top-tariff-name">{tariff.tariffName || `Tariff #${tariff.tariffId}`}</div>
              <div className="top-tariff-meta">{formatCount(tariff.purchasesCount)} успешных покупок</div>
              <div className="top-tariff-revenue">{formatMoney(tariff.revenueRub, 'RUB')}</div>
              <div className="top-tariff-meta">Продано: {formatHoursValue((Number(tariff.soldMs) || 0) / 3600000)}</div>
            </div>
          ))}
        </div>
      )}
      {purchases.length === 0 ? (
        <div className="empty-message">Нет покупок</div>
      ) : (
        <>
          <div className="purchases-table-wrapper">
            <table className="purchases-table">
              <thead>
                <tr>
                  <th>Пользователь</th>
                  <th>Тариф</th>
                  <th>UUID</th>
                  <th>Дата</th>
                  <th>Сумма</th>
                  <th>Объём</th>
                  <th>Статус</th>
                </tr>
              </thead>
              <tbody>
                {purchases.map((payment, idx) => (
                  <tr key={`${payment.id}-${payment.purchaseUUID}-${idx}`} className="purchase-row">
                    <td>
                      <button
                        className="user-link"
                        onClick={() => onUserClick(payment.user?.id)}
                      >
                        {payment.user?.firstName || payment.user?.username || `#${payment.user?.id || '—'}`}
                      </button>
                    </td>
                    <td>
                      <div className="purchase-tariff">
                        <div>{payment.tariffName || `Tariff #${payment.tariffId}`}</div>
                        <div className="purchase-secondary">ID: {payment.tariffId ?? '—'}</div>
                      </div>
                    </td>
                    <td>
                      <div className="purchase-secondary">{payment.purchaseUUID || '—'}</div>
                    </td>
                    <td>
                      <div className="purchase-date">
                        {formatDate(payment.createdAt)}
                      </div>
                    </td>
                    <td>
                      <div className="purchase-amount">
                        {formatMoney(payment.pricePaidRub, 'RUB')}
                      </div>
                    </td>
                    <td>
                      <div className="purchase-secondary">{formatHoursValue((Number(payment.msAmount) || 0) / 3600000)}</div>
                    </td>
                    <td>
                      <span className={`status-badge status-${payment.status || 'unknown'}`}>
                        {formatPaymentStatus(payment.status)}
                      </span>
                      {payment.confirmationUrl && (
                        <a href={payment.confirmationUrl} target="_blank" rel="noopener noreferrer" className="confirmation-link">
                          Оплатить
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={loadPurchases}
            />
          )}
        </>
      )}
    </div>
  );
};

export default RecentPurchases;
